import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import { track } from '@/lib/analytics'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { hasFluentEmoji } from '@/lib/fluent-emoji'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { SwipeableRow } from '@/components/ui/swipeable-row'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { hapticBellRing, playNotificationSound } from '@/lib/native'
import { syncNotifications } from '@/lib/native/push'
import { toast } from 'sonner'
import { Bell } from '@phosphor-icons/react'
import type { Notification, UserProfile } from '@/types/database'

type NotificationWithActor = Notification & {
  actor?: UserProfile | null
}

// Helper component to render text with FluentEmoji
// Automatically uses FluentEmoji for any emoji that has a mapping in the fluent-emoji library
function TextWithEmoji({ text, className }: { text: string; className?: string }) {
  const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keyIndex = 0

  emojiRegex.lastIndex = 0

  while ((match = emojiRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const emoji = match[0]
    if (hasFluentEmoji(emoji)) {
      parts.push(
        <FluentEmoji
          key={`emoji-${keyIndex++}`}
          emoji={emoji}
          size={16}
          className="inline-block align-text-bottom mx-0.5"
        />
      )
    } else {
      parts.push(emoji)
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return <span className={className}>{parts}</span>
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'comment': return '💬'
    case 'reaction': return '❤️'
    case 'milestone': return '🔥'
    case 'conquest': return '🏆'
    case 'habit_created': return '📋'
    case 'challenge_completed': return '🎯'
    case 'new_follower': return '👤'
    case 'habit_reminder': return '⏰'
    case 'admin_broadcast': return '📢'
    case 'admin_message': return '💌'
    case 'partner_request': return '🤝'
    case 'partner_accepted': return '🎉'
    case 'partner_completed': return '✅'
    case 'partner_streak': return '🔥'
    case 'partner_nudge': return '💪'
    default: return '🔔'
  }
}

const shakeAnimation = {
  shake: {
    rotate: [0, -15, 15, -15, 15, -10, 10, -5, 5, 0],
    transition: {
      duration: 0.6,
      ease: "easeInOut" as const
    }
  }
}

interface NotificationBellProps {
  className?: string
  size?: number
}

export function NotificationBell({ className, size = 20 }: NotificationBellProps) {
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const [shouldShake, setShouldShake] = useState(false)

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationWithActor[]>([])
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [markingAll, setMarkingAll] = useState(false)
  const [clearingAll, setClearingAll] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  // ─── Unread count polling (runs always) ───
  useEffect(() => {
    loadUnreadCount()

    const interval = setInterval(loadUnreadCount, 30000)
    const handleFocus = () => loadUnreadCount()
    window.addEventListener('focus', handleFocus)
    const handleNotificationsUpdated = () => loadUnreadCount()
    window.addEventListener('notifications-updated', handleNotificationsUpdated)

    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('notifications-updated', handleNotificationsUpdated)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadUnreadCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false)

      const newCount = count || 0

      if (newCount > unreadCount && !isLoading) {
        setShouldShake(true)
        playNotificationSound()
        setTimeout(() => setShouldShake(false), 1000)
      }
      setUnreadCount(newCount)
    } catch (error) {
      console.error('Error loading unread count:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (unreadCount > 0 && !isLoading) {
      const shakeInterval = setInterval(() => {
        setShouldShake(true)
        setTimeout(() => setShouldShake(false), 700)
      }, 5000)

      return () => clearInterval(shakeInterval)
    }
  }, [unreadCount, isLoading])

  // ─── Load full notifications when drawer opens ───
  const loadNotifications = useCallback(async () => {
    setNotificationsLoading(true)

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setNotificationsLoading(false)
      return
    }

    syncNotifications()

    const { data } = await supabase
      .from('notifications')
      .select(`
        *,
        actor:user_profiles!notifications_actor_id_fkey(*)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50)

    if (data) {
      setNotifications(data as unknown as NotificationWithActor[])
      setUnreadCount(data.filter(n => !n.is_read).length)

      // Auto-mark all as read
      if (data.some(n => !n.is_read)) {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('user_id', user.id)
          .eq('is_read', false)

        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
        setUnreadCount(0)
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      }
    }

    setNotificationsLoading(false)
  }, [])

  // When drawer opens, load notifications
  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open)
    if (open) {
      track('notification_bell_opened', { unread_count: unreadCount }, 'notification')
      hapticBellRing()
      loadNotifications()
    } else {
      setShowClearConfirm(false)
    }
  }

  // ─── Actions ───
  const handleMarkAllAsRead = async () => {
    setMarkingAll(true)

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false)

    if (!error) {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
      toast.success('All notifications marked as read')
      window.dispatchEvent(new CustomEvent('notifications-updated'))
    } else {
      toast.error('Failed to mark all as read')
    }

    setMarkingAll(false)
  }

  const handleClearAll = async () => {
    setClearingAll(true)

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setClearingAll(false)
      return
    }

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', user.id)

    if (!error) {
      setNotifications([])
      setUnreadCount(0)
      setShowClearConfirm(false)
      toast.success('All notifications cleared')
      window.dispatchEvent(new CustomEvent('notifications-updated'))
    } else {
      toast.error('Failed to clear notifications')
    }

    setClearingAll(false)
  }

  const handleNotificationClick = async (notification: NotificationWithActor) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notification.id)

      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    }

    // Close drawer, then navigate
    setDrawerOpen(false)

    if (notification.type === 'habit_reminder') {
      if (notification.reference_type === 'habit' && notification.reference_id) {
        navigate(`/?habit=${notification.reference_id}`)
      } else {
        navigate('/')
      }
    } else if (notification.reference_type === 'milestone' && notification.reference_id) {
      navigate('/community')
    } else if (notification.reference_type === 'habit' && notification.reference_id) {
      navigate(`/?habit=${notification.reference_id}`)
    } else if (notification.reference_type === 'challenge' && notification.reference_id) {
      navigate('/challenges')
    } else if (notification.reference_type === 'user' && notification.actor?.username) {
      navigate(`/u/${notification.actor.username}`)
    } else if (notification.reference_type === 'user') {
      navigate('/community')
    } else if (notification.type === 'partner_request' || notification.type === 'partner_accepted' || notification.type === 'partner_completed' || notification.type === 'partner_streak' || notification.type === 'partner_nudge') {
      navigate('/community?tab=partner')
    }
  }

  const handleDelete = async (notificationId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)

    if (!error) {
      const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
      toast.success('Notification deleted')
      window.dispatchEvent(new CustomEvent('notifications-updated'))
    } else {
      toast.error('Failed to delete notification')
    }
  }

  const hasUnread = unreadCount > 0
  const shouldAnimate = isHovered || shouldShake

  return (
    <Drawer open={drawerOpen} onOpenChange={handleDrawerOpenChange}>
      <DrawerTrigger asChild>
        <button
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={cn(
            "relative inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 w-10",
            className
          )}
        >
          <motion.div
            animate={shouldAnimate ? "shake" : ""}
            variants={shakeAnimation}
            style={{ transformOrigin: "top center" }}
          >
            <Bell size={size} className="text-slate-700 dark:text-slate-200" />
          </motion.div>

          {/* Badge with unread count */}
          <AnimatePresence>
            {hasUnread && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </DrawerTrigger>

      <DrawerContent className="h-[70vh] flex flex-col">
        {/* Header */}
        <DrawerHeader className="pb-2">
          <div className="flex items-center justify-center gap-2">
            <FluentEmoji emoji="🔔" size={20} />
            <DrawerTitle>Notifications</DrawerTitle>
          </div>
          {notifications.length > 0 && (
            <DrawerDescription>
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </DrawerDescription>
          )}
        </DrawerHeader>

        {/* Actions bar */}
        {notifications.length > 0 && !notificationsLoading && (
          <div className="flex items-center justify-between gap-2 px-4 pb-2">
            {unreadCount > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={markingAll}
                className="text-xs"
              >
                {markingAll ? (
                  <>
                    <Spinner size="sm" className="mr-1.5" />
                    Marking...
                  </>
                ) : (
                  <>
                    <FluentEmoji emoji="✅" size={12} className="mr-1.5" />
                    Mark all read
                  </>
                )}
              </Button>
            ) : <div />}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowClearConfirm(true)}
              disabled={clearingAll}
              className="text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
            >
              {clearingAll ? (
                <Spinner size="sm" className="mr-1.5" />
              ) : (
                <FluentEmoji emoji="🗑️" size={12} className="mr-1.5" />
              )}
              Clear all
            </Button>
          </div>
        )}

        {/* Scrollable notification list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {notificationsLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner size="lg" />
              <p className="text-sm text-slate-500 mt-3">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="relative mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 15, delay: 0.1 }}
                  className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center border border-slate-200 dark:border-slate-700"
                >
                  <FluentEmoji emoji="🔕" size={40} />
                </motion.div>
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                No notifications yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm text-center mb-4">
                You'll be notified when friends react to your milestones or achieve something awesome!
              </p>
              <DrawerClose asChild>
                <Link to="/community">
                  <Button
                    size="sm"
                    className="gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 rounded-xl"
                  >
                    <FluentEmoji emoji="👥" size={16} />
                    Explore Community
                  </Button>
                </Link>
              </DrawerClose>
            </div>
          ) : (
            /* Notification list */
            <div className="space-y-2">
              <AnimatePresence mode="popLayout">
                {notifications.map((notification, index) => (
                  <SwipeableRow
                    key={notification.id}
                    leftAction={{
                      icon: '🗑️',
                      label: 'Delete',
                      color: 'text-white',
                      backgroundColor: '#ef4444',
                      onAction: () => handleDelete(notification.id),
                    }}
                    threshold={80}
                    maxSwipe={120}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.02 }}
                      onClick={() => handleNotificationClick(notification)}
                      className={`
                        p-3 rounded-xl cursor-pointer transition-all relative group border
                        ${!notification.is_read
                          ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-950/30'
                          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                        }
                      `}
                    >
                      {/* Unread indicator */}
                      {!notification.is_read && (
                        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-amber-500 rounded-full" />
                      )}

                      <div className="flex items-start gap-2.5 pl-3">
                        {/* Actor Avatar or Icon */}
                        {notification.actor ? (
                          <Avatar className="w-9 h-9 shrink-0">
                            <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs">
                              {notification.actor.profile_emoji ? (
                                <FluentEmoji emoji={notification.actor.profile_emoji} size={20} />
                              ) : (
                                notification.actor.display_name?.[0]?.toUpperCase() ||
                                notification.actor.username?.[0]?.toUpperCase() ||
                                '?'
                              )}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center shrink-0">
                            <FluentEmoji emoji={getNotificationIcon(notification.type)} size={18} />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white break-words">
                            <TextWithEmoji text={notification.title} />
                          </p>
                          {notification.body && (
                            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 break-words line-clamp-2">
                              <TextWithEmoji text={notification.body} />
                            </p>
                          )}
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>

                        {/* Delete button (visible on hover/desktop) */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleDelete(notification.id, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 shrink-0 w-8 h-8"
                        >
                          <FluentEmoji emoji="🗑️" size={14} />
                        </Button>
                      </div>
                    </motion.div>
                  </SwipeableRow>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Clear all confirmation (nested drawer) */}
        <Drawer open={showClearConfirm} onOpenChange={setShowClearConfirm}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Clear all notifications?</DrawerTitle>
              <DrawerDescription>
                This action cannot be undone. This will permanently delete all your notifications.
              </DrawerDescription>
            </DrawerHeader>
            <DrawerFooter>
              <Button
                onClick={handleClearAll}
                disabled={clearingAll}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {clearingAll ? <Spinner size="sm" className="mr-2" /> : null}
                Clear All
              </Button>
              <DrawerClose asChild>
                <Button variant="outline">Cancel</Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </DrawerContent>
    </Drawer>
  )
}
